import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertTriangle, Download, Lock, Trash2, Undo2 } from 'lucide-react'
import { downloadFile } from '@/lib/download'
import { bestaetige, frage, hinweis } from '@/lib/dialoge'
import { fieldLabel, formCard, inputBase } from '@/lib/form-styles'
import {
  AKTION_TEXT,
  mandantenendeApi,
  ortText,
  protokollText,
  type EndeStand,
  type ExportDateien,
} from '../api/mandantenende.api'

/**
 * AP-42 — Reiter „Mandantenende" am Mandanten in der Betreiber-Konsole.
 *
 * Drei Schritte von oben nach unten, in der Reihenfolge, in der sie gebraucht
 * werden: Daten herausgeben, Löschung vormerken, endgültig löschen. Der letzte
 * ist bewusst kein Dialog mit „OK": Wer löscht, tippt die Subdomain ab und gibt
 * sein Passwort ein — geprüft wird beides am Server.
 */

function meldung(e: unknown, ersatz: string): string {
  const antwort = (e as { response?: { data?: { message?: string | string[] } } })?.response?.data?.message
  return Array.isArray(antwort) ? antwort.join(' · ') : (antwort ?? ersatz)
}

const tag = (wert: string) =>
  new Date(wert).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
const zeitpunkt = (wert: string) =>
  new Date(wert).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })

function groesse(bytes: number): string {
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toLocaleString('de-DE', { maximumFractionDigits: 1 })} GB`
  if (bytes >= 1024 ** 2) return `${(bytes / 1024 ** 2).toLocaleString('de-DE', { maximumFractionDigits: 1 })} MB`
  return `${Math.ceil(bytes / 1024).toLocaleString('de-DE')} KB`
}

const knopf =
  'inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium disabled:pointer-events-none disabled:opacity-50'

export default function MandantenendeReiter({ tenantId }: { tenantId: string }) {
  const navigate = useNavigate()
  const [stand, setStand] = useState<EndeStand | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState<'export' | 'vormerken' | 'loeschen' | null>(null)

  const [subdomain, setSubdomain] = useState('')
  const [passwort, setPasswort] = useState('')
  const [grund, setGrund] = useState('')
  // AP-11-E5: nur bei eigenem Speicher — Vorgabe ist das Verzeichnis (T-12).
  const [exportDateien, setExportDateien] = useState<ExportDateien>('VERZEICHNIS')

  const lade = useCallback(async () => {
    try {
      setStand(await mandantenendeApi.stand(tenantId))
      setFehler(null)
    } catch (e) {
      setFehler(meldung(e, 'Der Stand konnte nicht geladen werden.'))
    }
  }, [tenantId])

  useEffect(() => {
    void lade()
  }, [lade])

  if (!stand) {
    return <p className={fehler ? 'text-sm text-destructive' : 'text-sm text-muted-foreground'}>{fehler ?? 'Laden…'}</p>
  }

  const { tenant, vormerkung, bestand } = stand
  const eigen = stand.eigenerSpeicher ?? []
  const hatEigenen = eigen.length > 0

  async function exportieren() {
    setLaeuft('export')
    setFehler(null)
    try {
      const { daten, dateiname } = await mandantenendeApi.gesamtexport(tenantId, hatEigenen ? exportDateien : undefined)
      downloadFile(daten, dateiname, 'application/zip')
      await lade()
    } catch (e) {
      setFehler(meldung(e, 'Der Gesamtexport ist gescheitert.'))
    } finally {
      setLaeuft(null)
    }
  }

  async function vormerken() {
    const text = await frage(
      `Löschung von „${tenant.name}“ vormerken?\n\nDer Zugang wird sofort gesperrt — niemand aus dem Betrieb kann sich mehr anmelden. ` +
        'Gelöscht wird noch nichts. Grund (steht im Protokoll):',
      '',
      { pflicht: true, ok: 'Vormerken' },
    )
    if (!text) return
    setLaeuft('vormerken')
    try {
      setStand(await mandantenendeApi.vormerken(tenantId, text))
      setFehler(null)
    } catch (e) {
      setFehler(meldung(e, 'Die Vormerkung ist gescheitert.'))
    } finally {
      setLaeuft(null)
    }
  }

  async function zuruecknehmen() {
    const ok = await bestaetige(
      `Vormerkung für „${tenant.name}“ zurücknehmen?\n\nDer Zugang ist danach wieder offen.`,
      { ok: 'Zurücknehmen' },
    )
    if (!ok) return
    setLaeuft('vormerken')
    try {
      setStand(await mandantenendeApi.zuruecknehmen(tenantId))
      setFehler(null)
    } catch (e) {
      setFehler(meldung(e, 'Die Vormerkung konnte nicht zurückgenommen werden.'))
    } finally {
      setLaeuft(null)
    }
  }

  async function loeschen() {
    if (!vormerkung) return
    const ok = await bestaetige(
      `„${tenant.name}“ jetzt endgültig löschen?\n\n` +
        `${bestand.benutzer} Benutzer, ${bestand.zeitbuchungen} Zeitbuchungen, ${bestand.belege} Belege, ${bestand.dateien} Dateien — ` +
        'aus der Datenbank und aus dem Dateispeicher. Das lässt sich nicht rückgängig machen.' +
        (hatEigenen
          ? `\n\nDie Dateien im eigenen Speicher des Betriebs (${ortText(eigen)}) werden nicht angefasst — sie bleiben beim Kunden.`
          : '') +
        (stand?.letzterExportAm ? '' : '\n\nFür diesen Mandanten wurde noch kein Gesamtexport erstellt.'),
      { ok: 'Endgültig löschen', gefaehrlich: true },
    )
    if (!ok) return
    setLaeuft('loeschen')
    setFehler(null)
    try {
      const erg = await mandantenendeApi.loeschen(tenantId, {
        confirmSubdomain: subdomain,
        password: passwort,
        ...(vormerkung.wartefristVorbei ? {} : { shortenReason: grund }),
      })
      await hinweis(
        `„${erg.name}“ ist gelöscht: ${erg.zeilen.toLocaleString('de-DE')} Datensätze, ${erg.dateien.toLocaleString('de-DE')} Dateien, ` +
          `${erg.anmeldekonten} Anmeldekonten. ` +
          (erg.beimKundenBelassen?.length ? `Die Dateien im eigenen Speicher (${ortText(erg.beimKundenBelassen)}) liegen weiter beim Kunden. ` : '') +
          'Der Vorgang steht im Löschprotokoll.',
      )
      navigate('/admin/loeschprotokoll')
    } catch (e) {
      setPasswort('')
      setFehler(meldung(e, 'Die Löschung ist gescheitert.'))
      await lade()
    } finally {
      setLaeuft(null)
    }
  }

  const darfLoeschen =
    !!vormerkung &&
    subdomain.trim() === tenant.subdomain &&
    passwort.length > 0 &&
    (vormerkung.wartefristVorbei || grund.trim().length >= 10)

  return (
    <div className="space-y-6">
      {fehler && <div className="rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">{fehler}</div>}

      {tenant.isOperator && (
        <div className="rounded-md border border-border bg-muted/30 px-4 py-3 text-sm text-muted-foreground">
          Das ist der Betreiber-Mandant. Er lässt sich exportieren, aber weder vormerken noch löschen.
        </div>
      )}

      {/* ── Bestand ── */}
      <div className={formCard}>
        <h2 className="text-base font-semibold text-foreground">Bestand</h2>
        <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-3 lg:grid-cols-6">
          {(
            [
              ['Benutzer', bestand.benutzer],
              ['Geschäftspartner', bestand.kunden],
              ['Projekte', bestand.projekte],
              ['Zeitbuchungen', bestand.zeitbuchungen],
              ['Belege', bestand.belege],
              ['Dateien', bestand.dateien],
            ] as const
          ).map(([name, wert]) => (
            <div key={name}>
              <dt className="text-xs uppercase tracking-wide text-muted-foreground">{name}</dt>
              <dd className="tabular-nums text-foreground">
                {wert.toLocaleString('de-DE')}
                {name === 'Dateien' && bestand.dateiBytes > 0 && (
                  <span className="text-muted-foreground"> · {groesse(bestand.dateiBytes)}</span>
                )}
              </dd>
            </div>
          ))}
        </dl>
      </div>

      {/* ── 1. Gesamtexport ── */}
      <div className={formCard}>
        <h2 className="text-base font-semibold text-foreground">1. Daten herausgeben</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Eine ZIP-Datei mit allen Tabellen des Betriebs (JSON und CSV) und allen Dateien. Zugangsgeheimnisse wie
          Passwörter und Schlüssel sind nicht enthalten. Der Export geht jederzeit — auch ohne Vormerkung.
        </p>
        {!stand.speicherBereit && (
          <p className="mt-2 flex items-center gap-2 text-sm text-amber-700 dark:text-amber-400">
            <AlertTriangle size={14} /> Der Dateispeicher ist nicht erreichbar — der Export enthielte keine Dateien.
          </p>
        )}
        {hatEigenen && (
          <fieldset className="mt-3 space-y-1.5 rounded-md border border-border bg-background px-4 py-3 text-sm text-foreground">
            <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Eigener Speicher des Betriebs
            </legend>
            <p className="text-muted-foreground">
              Die Dateien dieses Betriebs liegen in seinem eigenen Speicher ({ortText(eigen)}). Der Kunde hat sie dort
              bereits — was soll der Export enthalten?
            </p>
            <label className="flex items-start gap-2">
              <input
                type="radio"
                name="export-dateien"
                className="mt-1"
                checked={exportDateien === 'VERZEICHNIS'}
                onChange={() => setExportDateien('VERZEICHNIS')}
              />
              <span>Nur ein Verzeichnis der Dateien (welche Datei wozu gehört und wo sie im Bucket liegt)</span>
            </label>
            <label className="flex items-start gap-2">
              <input
                type="radio"
                name="export-dateien"
                className="mt-1"
                checked={exportDateien === 'MIT'}
                onChange={() => setExportDateien('MIT')}
              />
              <span>Auch die Dateien selbst — der Export holt sie aus dem Speicher des Kunden und kann sehr groß werden</span>
            </label>
          </fieldset>
        )}
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => void exportieren()}
            disabled={laeuft !== null}
            className={`${knopf} bg-primary text-primary-foreground hover:bg-primary/90`}
          >
            <Download size={15} /> {laeuft === 'export' ? 'Export wird erstellt…' : 'Gesamtexport erstellen'}
          </button>
          <span className="text-sm text-muted-foreground">
            {stand.letzterExportAm ? `Zuletzt am ${zeitpunkt(stand.letzterExportAm)}` : 'Noch kein Export erstellt.'}
          </span>
        </div>
      </div>

      {/* ── 2. Vormerken ── */}
      {!tenant.isOperator && (
        <div className={formCard}>
          <h2 className="text-base font-semibold text-foreground">2. Löschung vormerken</h2>
          {vormerkung ? (
            <>
              <p className="mt-1 flex items-center gap-2 text-sm text-foreground">
                <Lock size={14} className="text-destructive" />
                Vorgemerkt am {tag(vormerkung.am)} —{' '}
                {vormerkung.durch === 'MANDANT' ? 'Löschantrag des Mandanten' : 'durch den Betreiber'}. Der Zugang ist
                gesperrt.
              </p>
              {vormerkung.grund && <p className="mt-1 text-sm text-muted-foreground">Grund: {vormerkung.grund}</p>}
              <p className={`mt-2 text-sm ${vormerkung.ueberfaellig ? 'font-medium text-destructive' : 'text-muted-foreground'}`}>
                Löschen frühestens ab {tag(vormerkung.fruehestens)} ({stand.wartefristTage} Tage Wartefrist), zugesagt
                bis {tag(vormerkung.spaetestens)}
                {vormerkung.ueberfaellig ? ' — die Frist ist abgelaufen.' : '.'}
              </p>
              <button
                type="button"
                onClick={() => void zuruecknehmen()}
                disabled={laeuft !== null}
                className={`${knopf} mt-3 border border-border text-foreground hover:bg-muted`}
              >
                <Undo2 size={15} /> Vormerkung zurücknehmen
              </button>
            </>
          ) : (
            <>
              <p className="mt-1 text-sm text-muted-foreground">
                Sperrt den Zugang des Betriebs sofort und startet die Fristen. Gelöscht wird dabei nichts; die
                Vormerkung lässt sich zurücknehmen.
              </p>
              <button
                type="button"
                onClick={() => void vormerken()}
                disabled={laeuft !== null}
                className={`${knopf} mt-3 border border-destructive/50 text-destructive hover:bg-destructive/10`}
              >
                <Lock size={15} /> Löschung vormerken
              </button>
            </>
          )}
        </div>
      )}

      {/* ── 3. Löschen ── */}
      {!tenant.isOperator && vormerkung && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-6">
          <h2 className="text-base font-semibold text-destructive">3. Endgültig löschen</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Entfernt den Betrieb aus der Datenbank und alle seine Dateien aus dem Speicher. Anmeldekonten von Personen,
            die auch in einem anderen Betrieb Mitglied sind, bleiben bestehen. In den Sicherungen verschwinden die
            Daten mit deren Ablauf.
          </p>
          {hatEigenen && (
            <p className="mt-2 text-sm text-foreground">
              Dieser Betrieb hat eigenen Speicher ({ortText(eigen)}). Die Dateien dort gehören dem Kunden und bleiben
              bei ihm — Symphenia löscht in fremder Infrastruktur nichts. Entfernt werden die Verweise darauf und die
              hinterlegten Zugangsdaten.
            </p>
          )}
          <div className="mt-4 grid max-w-xl gap-3">
            <label className="space-y-1">
              <span className={fieldLabel}>
                Subdomain abtippen: <span className="font-mono normal-case text-foreground">{tenant.subdomain}</span>
              </span>
              <input
                className={inputBase}
                value={subdomain}
                onChange={(e) => setSubdomain(e.target.value)}
                autoComplete="off"
                spellCheck={false}
              />
            </label>
            {!vormerkung.wartefristVorbei && (
              <label className="space-y-1">
                <span className={fieldLabel}>
                  Wartefrist läuft bis {tag(vormerkung.fruehestens)} — Begründung für das frühere Löschen
                </span>
                <input
                  className={inputBase}
                  value={grund}
                  onChange={(e) => setGrund(e.target.value)}
                  placeholder="z. B. Kunde verlangt schriftlich die sofortige Löschung"
                />
              </label>
            )}
            <label className="space-y-1">
              <span className={fieldLabel}>Dein Passwort</span>
              <input
                className={inputBase}
                type="password"
                value={passwort}
                onChange={(e) => setPasswort(e.target.value)}
                autoComplete="current-password"
              />
            </label>
          </div>
          <button
            type="button"
            onClick={() => void loeschen()}
            disabled={!darfLoeschen || laeuft !== null}
            className={`${knopf} mt-4 bg-destructive text-destructive-foreground hover:bg-destructive/90`}
          >
            <Trash2 size={15} /> {laeuft === 'loeschen' ? 'Wird gelöscht…' : 'Endgültig löschen'}
          </button>
        </div>
      )}

      {/* ── Protokoll ── */}
      {stand.protokoll.length > 0 && (
        <div className={formCard}>
          <h2 className="text-base font-semibold text-foreground">Protokoll</h2>
          <ul className="mt-3 space-y-1.5 text-sm">
            {stand.protokoll.map((z) => (
              <li key={z.id} className="flex flex-wrap gap-x-3">
                <span className="tabular-nums text-muted-foreground">{zeitpunkt(z.createdAt)}</span>
                <span className="font-medium text-foreground">{AKTION_TEXT[z.action] ?? z.action}</span>
                <span className="text-muted-foreground">{protokollText(z)}</span>
                <span className="text-muted-foreground">· {z.operatorEmail ?? '—'}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
